import type { VisualType } from "@/lib/types";

/**
 * Algolia stores only the Sanity image asset `_ref` for each record (see the
 * `resolveImageRef` helper in `packages/algolia` transform), not the full
 * `visual` object the page-builder cards are built around.
 *
 * Directory cards render their image through `VisualWrapper` → `ImageWrapper`,
 * which only ever read `mediaSource.customImage.image.asset._ref` (plus
 * optional `hotspot`/`alt`/`caption`). Reconstructing that minimal subtree lets
 * the Algolia-backed directory reuse the exact same Sanity image pipeline
 * (responsive URLs, loader, crop) as the GROQ-fed cards, so there is no visual
 * drift between data sources.
 *
 * Crop / hotspot / alt aren't indexed; the image component falls back to a
 * centre crop and empty alt, which matches how these cards already behave when
 * those fields are absent in Sanity.
 */
export function visualFromImageRef(
  imageRef?: string | null,
): VisualType | null {
  if (!imageRef) return null;

  return {
    _type: "visual",
    mediaSource: {
      _type: "visualImageItem",
      customImage: {
        _type: "customImage",
        image: {
          _type: "image",
          asset: { _ref: imageRef, _type: "reference" },
        },
      },
    },
    // The runtime shape above is a faithful subset of `VisualType`, but the
    // generated type is far larger (every optional field). Cast through
    // `unknown` to keep this the single, documented place where that gap lives.
  } as unknown as VisualType;
}
